<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  DxDataGrid,
  DxColumn,
  DxSorting,
  DxScrolling,
  DxButton as DxGridButton,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import DxTabPanel, { DxItem as DxTabItem } from 'devextreme-vue/tab-panel';
import DxForm, { DxSimpleItem, DxLabel } from 'devextreme-vue/form';
import { BottoPagePanel, ConfirmDialog, bottoGridPaging, showError, showSuccess } from '@botto/core';
import { buyTemplatesStore } from '../api';
import type { BuyTemplate, BuyTemplateCreateRequest, BuyTemplateUpdateRequest, AggregatorSmsTypeMap } from '../api';
import {
  buyTemplateCallsToArray,
  buyTemplateCallsToMap,
  buyTemplateSmsFromPrices,
  buyTemplateSmsToPrices,
  type BuyTemplateCallEntry,
} from '../api/normalizers';
import BuyTemplateSmsPricesEditor from './editors/BuyTemplateSmsPricesEditor.vue';
import BuyTemplateCallsPricesEditor from './editors/BuyTemplateCallsPricesEditor.vue';

interface BuyTemplateFormModel {
  id?: number;
  name: string;
  smsPrices: AggregatorSmsTypeMap;
  callEntries: BuyTemplateCallEntry[];
}

const grid = ref<InstanceType<typeof DxDataGrid> | null>(null);
const popupOpen = ref(false);
const popupMode = ref<'create' | 'edit'>('create');
const formData = ref<BuyTemplateFormModel>(emptyForm());
const saving = ref(false);

const confirmOpen = ref(false);
const confirmTarget = ref<BuyTemplate | null>(null);

const popupTitle = computed(() =>
  popupMode.value === 'create'
    ? 'Новый шаблон закупки'
    : `Шаблон «${formData.value.name}» (#${formData.value.id ?? ''})`,
);

function emptyForm(): BuyTemplateFormModel {
  return { name: '', smsPrices: {}, callEntries: [] };
}

function refresh() {
  grid.value?.instance?.refresh();
}

function openCreate() {
  popupMode.value = 'create';
  formData.value = emptyForm();
  popupOpen.value = true;
}

async function openEdit(row: BuyTemplate) {
  popupMode.value = 'edit';
  try {
    const fresh = (await buyTemplatesStore.byKey?.(row.id)) ?? row;
    formData.value = {
      id: fresh.id,
      name: fresh.name,
      smsPrices: buyTemplateSmsFromPrices(fresh.prices),
      callEntries: buyTemplateCallsToArray(fresh.prices?.calls),
    };
    popupOpen.value = true;
  } catch (e) {
    showError(e);
  }
}

function confirmDelete(row: BuyTemplate) {
  confirmTarget.value = row;
  confirmOpen.value = true;
}

async function doDelete() {
  if (!confirmTarget.value) return;
  try {
    await buyTemplatesStore.remove?.(confirmTarget.value.id);
    showSuccess('Шаблон удалён');
    confirmOpen.value = false;
    confirmTarget.value = null;
    refresh();
  } catch (e) {
    showError(e);
  }
}

function buildPrices() {
  return {
    sms: buyTemplateSmsToPrices(formData.value.smsPrices),
    calls: buyTemplateCallsToMap(formData.value.callEntries),
  };
}

async function save() {
  if (!formData.value.name.trim()) {
    showError('Укажите название шаблона');
    return;
  }
  saving.value = true;
  try {
    const prices = buildPrices();
    if (popupMode.value === 'create') {
      const body: BuyTemplateCreateRequest = {
        name: formData.value.name.trim(),
        prices,
      };
      await buyTemplatesStore.insert(body as unknown as BuyTemplate);
      showSuccess('Шаблон создан');
    } else {
      const body: BuyTemplateUpdateRequest = {
        id: formData.value.id!,
        name: formData.value.name.trim(),
        prices,
      };
      await buyTemplatesStore.update(formData.value.id!, body as unknown as BuyTemplate);
      showSuccess('Шаблон обновлён');
    }
    popupOpen.value = false;
    refresh();
  } catch (e) {
    showError(e);
  } finally {
    saving.value = false;
  }
}

const pageSizes = [10, 20, 50, 100];
const { paging, pager } = bottoGridPaging({ allowedPageSizes: pageSizes });

type GridRowEvent = { data?: BuyTemplate };
type GridButtonEvent = { row?: { data?: BuyTemplate } };

function handleRowDblClick(e: GridRowEvent): void {
  if (e.data) void openEdit(e.data);
}
function handleEditButton(e: GridButtonEvent): void {
  if (e.row?.data) void openEdit(e.row.data);
}
function handleDeleteButton(e: GridButtonEvent): void {
  if (e.row?.data) confirmDelete(e.row.data);
}
</script>

<template>
  <BottoPagePanel title="Шаблоны цен закупки" standalone>
    <template #actions>
      <DxButton icon="add" text="Добавить" type="default" styling-mode="contained" @click="openCreate" />
      <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="refresh" />
    </template>

    <DxDataGrid
      ref="grid"
      :data-source="buyTemplatesStore"
      key-expr="id"
      :remote-operations="{ paging: true, sorting: true, filtering: false }"
      :show-borders="false"
      :show-row-lines="false"
      :show-column-lines="false"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
      :paging="paging"
      :pager="pager"
      height="100%"
      width="100%"
      @row-dbl-click="handleRowDblClick"
    >
      <DxColumn data-field="id" caption="ID" data-type="number" :width="80" />
      <DxColumn data-field="name" caption="Название" />
      <DxColumn data-field="created_at" caption="Создан" data-type="datetime" :width="160" />
      <DxColumn data-field="updated_at" caption="Обновлён" data-type="datetime" :width="160" />

      <DxColumn type="buttons" :width="100" caption="Действия">
        <DxGridButton hint="Редактировать" icon="edit" :on-click="handleEditButton" />
        <DxGridButton hint="Удалить" icon="trash" :on-click="handleDeleteButton" />
      </DxColumn>

      <DxSorting mode="single" />
      <DxScrolling mode="standard" />
    </DxDataGrid>
  </BottoPagePanel>

  <DxPopup
    v-model:visible="popupOpen"
    :title="popupTitle"
    :show-close-button="true"
    :drag-enabled="false"
    :resize-enabled="false"
    width="80%"
    height="80%"
  >
    <DxTabPanel :animation-enabled="true">
      <DxTabItem title="Основное">
        <div class="tab-body">
          <DxForm :form-data="formData" :col-count="1" label-location="top">
            <DxSimpleItem data-field="name" :is-required="true">
              <DxLabel text="Название" />
            </DxSimpleItem>
          </DxForm>
        </div>
      </DxTabItem>

      <DxTabItem title="Цены SMS">
        <div class="tab-body">
          <BuyTemplateSmsPricesEditor v-model="formData.smsPrices" />
        </div>
      </DxTabItem>

      <DxTabItem title="Цены звонков">
        <div class="tab-body">
          <BuyTemplateCallsPricesEditor v-model="formData.callEntries" />
        </div>
      </DxTabItem>
    </DxTabPanel>

    <DxToolbarItem
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{ text: 'Сохранить', type: 'default', stylingMode: 'contained', disabled: saving, onClick: save }"
    />
    <DxToolbarItem
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{ text: 'Отмена', stylingMode: 'outlined', onClick: () => (popupOpen = false) }"
    />
  </DxPopup>

  <ConfirmDialog
    v-if="confirmOpen"
    v-model:visible="confirmOpen"
    title="Удалить шаблон?"
    :message="`Шаблон «${confirmTarget?.name}» (#${confirmTarget?.id}) будет удалён.`"
    ok-text="Удалить"
    ok-type="danger"
    @ok="doDelete"
  />
</template>

<style scoped>
.tab-body { padding: 16px 4px 0; }
</style>
